import { CircleHelp } from "lucide-react";
import type { ReactNode } from "react";
import type { OperatorCaseProgress } from "./operator-case-progress";

type Props = {
  progress: OperatorCaseProgress;
  retained: boolean;
  qualityLabel: string;
  rate: { value: string | null; unit: string; target: string | null; label: string; retained: boolean };
  timeLabel: string;
  timeValue: string;
  downtime: string;
  scrap: ReactNode;
  finish: string;
  packagingAction?: ReactNode;
};

const number = (value: number | null) => value === null ? "—" : new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(value);

/** Machine totals stay visibly separate from run progress and plan comparisons. */
export function OperatorProductionOverview({ progress, retained, qualityLabel, rate, timeLabel, timeValue, downtime, scrap, finish, packagingAction }: Props) {
  const isCaseUnit = progress.unit === "koli";
  const completedCases = progress.quantitySource !== undefined;
  const machineTotal = progress.quantitySource === "COMPLETED_CASES_MACHINE";
  const fill = progress.percent === null ? null : Math.min(100, Math.max(0, progress.percent));
  return <section className="operator-production-overview" aria-label="İşin üretim miktarı ve durumu">
    <div className="operator-case-progress">
      <header><h2>{machineTotal ? "Makine toplamı" : "Bu işin üretimi"}</h2><span>{retained ? "Son doğrulanmış miktar" : completedCases ? "Tamamlanan koli sayacı" : "Sunucuda kayıtlı miktar"}</span></header>
      <dl className="operator-case-numbers">
        <div className="operator-case-produced">
          <dt>{completedCases ? "Tamamlanan koli" : isCaseUnit ? "Üretilen koli karşılığı" : "Üretilen"}</dt>
          <dd>{number(progress.actual)} <span>{progress.unit}</span></dd>
          <small>{progress.actual === null ? completedCases ? "Koli ölçümü bekleniyor" : "Miktar doğrulanamadı" : machineTotal ? "Makinenin biriken koli sayısı; bu işe ait değildir" : completedCases ? "Bu işte tamamlanan tam koli sayısı" : isCaseUnit ? "Bu işe kaydedilen toplam üretim" : "Ürünün kayıtlı birimi"}</small>
        </div>
        <div><dt>İş emri miktarı</dt><dd>{number(progress.planned)} <span>{progress.unit}</span></dd></div>
        <div><dt>{progress.overrun !== null && progress.overrun > 0 ? "Planın üzerinde" : "Kalan"}</dt><dd>{number(progress.overrun !== null && progress.overrun > 0 ? progress.overrun : progress.remaining)} <span>{progress.unit}</span></dd></div>
      </dl>
      <div className="operator-case-progress-line">
        <div className="operator-case-track" role={fill === null ? undefined : "progressbar"} aria-label="Planın gerçekleşmesi" aria-valuemin={fill === null ? undefined : 0} aria-valuemax={fill === null ? undefined : 100} aria-valuenow={fill ?? undefined} aria-valuetext={progress.percent === null ? undefined : `Yüzde ${number(progress.percent)}${retained ? ", son doğrulanmış miktar" : ""}`}>
          {fill !== null ? <i style={{ width: `${fill}%` }} /> : null}
        </div>
        <strong>{progress.percent === null ? "—" : `%${number(progress.percent)}`}</strong>
      </div>
      {progress.conversionLabel || progress.issue || packagingAction ? <div className="operator-case-conversion">
        {progress.conversionLabel ? <strong>{progress.conversionLabel}</strong> : null}
        {progress.issue ? <p><CircleHelp size={16} aria-hidden="true" />{progress.issue}</p> : null}
        {packagingAction}
      </div> : null}
      <p className="operator-case-quality" role="status">{qualityLabel}</p>
    </div>
    <aside className="operator-case-vitals" aria-label="Hız ve süre">
      <div className="operator-case-speed">
        <div className="operator-case-speed-reading">
          <div><span>{rate.retained ? "Son geçerli hız" : "Üretim hızı"}</span><small>{rate.label}</small></div>
          <strong className={rate.value === null ? "is-pending" : undefined}>{rate.value === null ? "Ölçüm bekleniyor" : <>{rate.value} <small>{rate.unit.toLocaleLowerCase("tr-TR")}</small></>}</strong>
        </div>
        {rate.target !== null ? <p>Hedef hız <b>{rate.target} {rate.unit.toLocaleLowerCase("tr-TR")}</b></p> : null}
      </div>
      <dl className="operator-case-time"><div><dt>{timeLabel}</dt><dd>{timeValue}</dd></div><div><dt>Toplam duruş</dt><dd>{downtime}</dd></div><div><dt>Iskarta</dt><dd>{scrap}</dd></div></dl>
      <div className="operator-case-finish"><span>Tahmini bitiş</span><strong>{finish}</strong></div>
    </aside>
  </section>;
}
